import { useState } from "react";
import { Navigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { adminApi } from "@/lib/adminApi";
import { adminAuth } from "@/lib/adminAuth";
import { canManageQuestions } from "@/lib/roles";

export default function AdminCategories() {
  const qc = useQueryClient();
  const me = useQuery({ queryKey: ["admin-me"], queryFn: () => adminAuth.me(), retry: false });
  if (!me.isLoading && !canManageQuestions(me.data)) return <Navigate to="/admin" replace />;
  const list = useQuery({ queryKey: ["categories"], queryFn: () => adminApi.listCategories() });
  const [name, setName] = useState("");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState("");
  const [error, setError] = useState<string | null>(null);

  const invalidate = () => qc.invalidateQueries({ queryKey: ["categories"] });
  const showError = (e: any) => setError(e?.response?.data?.detail ?? "Operation failed.");

  const create = useMutation({
    mutationFn: () => adminApi.createCategory(name.trim()),
    onSuccess: () => { setName(""); setError(null); invalidate(); },
    onError: showError,
  });
  const update = useMutation({
    mutationFn: () => adminApi.updateCategory(editingId!, editingName.trim()),
    onSuccess: () => { setEditingId(null); setEditingName(""); setError(null); invalidate(); },
    onError: showError,
  });
  const remove = useMutation({
    mutationFn: (id: string) => adminApi.deleteCategory(id),
    onSuccess: () => { setError(null); invalidate(); },
    onError: showError,
  });

  return (
    <main className="mx-auto max-w-3xl p-6">
      <h1 className="mb-4 text-2xl font-bold text-brand-ink">Categories</h1>

      <div className="card mb-6 p-4">
        <label className="field-label">Add a category</label>
        <div className="mt-1 flex gap-2">
          <input className="field-input flex-1" value={name}
            placeholder="e.g. Biology"
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && name.trim()) create.mutate(); }} />
          <button className="btn-primary" disabled={!name.trim() || create.isPending}
            onClick={() => create.mutate()}>
            {create.isPending ? "Adding…" : "Add"}
          </button>
        </div>
        {error && <div className="mt-2 text-sm text-brand">{error}</div>}
      </div>

      <div className="card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-brand-light text-brand-ink">
            <tr>
              <th className="px-3 py-2 text-left font-semibold">Name</th>
              <th className="px-3 py-2 text-right font-semibold">Questions</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {list.data?.map((c) => (
              <tr key={c.id} className="border-t border-brand-ink/10">
                <td className="px-3 py-2">
                  {editingId === c.id ? (
                    <input className="field-input" value={editingName}
                      autoFocus
                      onChange={(e) => setEditingName(e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Enter" && editingName.trim()) update.mutate(); }} />
                  ) : c.name}
                </td>
                <td className="px-3 py-2 text-right">{c.questionCount}</td>
                <td className="px-3 py-2 text-right">
                  {editingId === c.id ? (
                    <div className="flex justify-end gap-2">
                      <button className="btn-secondary" onClick={() => { setEditingId(null); setEditingName(""); }}>Cancel</button>
                      <button className="btn-primary" onClick={() => update.mutate()} disabled={update.isPending}>Save</button>
                    </div>
                  ) : (
                    <div className="flex justify-end gap-3 text-sm">
                      <button className="text-brand hover:underline"
                        onClick={() => { setEditingId(c.id); setEditingName(c.name); }}>
                        Edit
                      </button>
                      <button
                        className="text-brand hover:underline disabled:opacity-40"
                        disabled={c.questionCount > 0 || remove.isPending}
                        title={c.questionCount > 0 ? "Cannot delete: category has questions" : ""}
                        onClick={() => { if (confirm(`Delete '${c.name}'?`)) remove.mutate(c.id); }}>
                        Delete
                      </button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
            {list.data?.length === 0 && (
              <tr><td colSpan={3} className="px-3 py-6 text-center text-brand-ink/60">
                No categories yet.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
    </main>
  );
}
